<script setup lang="ts">
/**
 * The signed-in layout.
 *
 * **One rail, no top bar.** The brand, the navigation and the account controls
 * are the same chrome, so they are one column on the inline-start edge rather
 * than a header that repeats the brand and parks identity in the far corner.
 * That column is the full height of the viewport and does not scroll, so
 * navigation is reachable from the bottom of a long table without scrolling
 * back up to find it.
 *
 * The four landmarks `FND-11` requires — `banner`, `navigation`, `main`,
 * `contentinfo` — are all still here and still on every route; `banner` is the
 * brand block at the head of the rail. The rail wrapper is a plain `div` on
 * purpose: `<aside>` would add a fifth `complementary` landmark wrapping two of
 * the four, which is worse than the markup it saves.
 *
 * Two other things the shell owns so screens do not have to remember them:
 *
 *   - **`data-density`.** DESIGN.md section 6 makes the type scale a per-surface
 *     decision. The decision is recorded in the catalog and applied here, so a
 *     compact screen cannot forget to be compact and a component cannot decide for
 *     itself.
 *   - **The viewport gate.** `ADM-*` screens declare `minWidth`; the gate is
 *     applied around the router view rather than inside five admin screens.
 */

import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

import AccountPanel from './AccountPanel.vue'
import AppNav from './AppNav.vue'
import Breadcrumb from './Breadcrumb.vue'
import DevToolbar from './DevToolbar.vue'
import { useRailShape } from './rail'

import brandMark from '@/assets/MatchPointIcon.svg'
import MpToaster from '@/components/base/MpToaster.vue'
import ErrorBoundary from '@/components/state/ErrorBoundary.vue'
import ViewportGate from '@/components/state/ViewportGate.vue'
import { useMainFocus } from '@/lib/route-focus'
import { SCREENS_BY_ID } from '@/screens/catalog'

const route = useRoute()
const { phone, labeled } = useRailShape()

const screen = computed(() => SCREENS_BY_ID[route.meta.screenId])

const main = ref<HTMLElement | null>(null)
useMainFocus(main)
</script>

<template>
  <div class="mp-shell">
    <!-- First focusable element in the document, per DESIGN.md section 8's
         keyboard requirement. Visible only when focused. -->
    <a
      class="mp-sr-only"
      href="#mp-main"
    >Skip to main content</a>

    <div class="mp-shell__body">
      <div
        class="mp-shell__rail"
        :class="{ 'mp-shell__rail--labeled': labeled }"
      >
        <header
          class="mp-shell__banner"
          role="banner"
        >
          <RouterLink
            class="mp-shell__brand"
            to="/home"
          >
            <!-- Decorative: the link's own text already names the destination, so
                 announcing the mark would only double it. -->
            <img
              class="mp-shell__mark"
              :src="brandMark"
              alt=""
              aria-hidden="true"
            >
            <span :class="labeled ? undefined : 'mp-sr-only'">Match&nbsp;Point</span>
          </RouterLink>
        </header>

        <AppNav />

        <AccountPanel :variant="phone ? 'bar' : labeled ? 'rail' : 'collapsed'" />
      </div>

      <div class="mp-shell__column">
        <DevToolbar />

        <main
          id="mp-main"
          ref="main"
          class="mp-shell__main"
          role="main"
          tabindex="-1"
          :data-density="screen.density === 'compact' ? 'compact' : undefined"
          :aria-label="screen.title"
        >
          <div class="mp-shell__content">
            <Breadcrumb :screen="screen" />
            <ErrorBoundary>
              <ViewportGate
                v-if="screen.minWidth !== undefined"
                :min-width="screen.minWidth"
                :screen-title="screen.title"
              >
                <RouterView />
              </ViewportGate>
              <RouterView v-else />
            </ErrorBoundary>
          </div>
        </main>

        <footer
          class="mp-shell__footer"
          role="contentinfo"
        >
          <p>Match Point · the behavioral-health treatment directory</p>
          <nav aria-label="Legal">
            <RouterLink to="/legal">
              Terms and privacy
            </RouterLink>
          </nav>
        </footer>
      </div>
    </div>

    <MpToaster />
  </div>
</template>

<style scoped>
.mp-shell {
  display: flex;
  flex-direction: column;
  min-block-size: 100vh;
  min-block-size: 100dvh;
}

/* The document keeps the scroll. A nested scrolling container was the other way
   to pin the rail, and it would have silently retired the router's
   `scrollBehavior`: `savedPosition` is a window offset, so Back onto a scrolled
   table would have landed at the top of it. */
.mp-shell__body {
  display: flex;
  flex: 1;
  min-block-size: 0;
}

.mp-shell__column {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-inline-size: 0;
}

.mp-shell__rail {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding-block: var(--space-3);
  padding-inline: var(--space-2);
  background: var(--surface-nav);
  color: var(--text-on-nav);
}

.mp-shell__banner {
  flex: none;
}

.mp-shell__brand {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-block-size: var(--target-min);
  padding-inline: var(--space-3);
  font-family: var(--font-heading);
  font-size: var(--font-size-h4);
  font-weight: var(--heading-weight);
  letter-spacing: var(--heading-tracking);
  color: var(--text-on-nav);
  text-decoration: none;
  white-space: nowrap;
}

.mp-shell__brand:visited,
.mp-shell__brand:hover {
  color: var(--text-on-nav);
}

/* With no wordmark beside it the mark is the whole banner, so it centers rather
   than sitting at a text inset — which also stops a 32px mark from setting the
   width of a rail whose other rows are 44px.

   The wordmark goes wherever labels do, which includes the phone bar: the
   account cluster there keeps full 44px touch cells, and a 44px switcher, a
   button, an avatar and "Match Point" do not fit across 390px. The mark plus
   the banner's own accessible name is what survives the cut, because the app's
   name is not what a clinician on a phone is short of. */
.mp-shell__rail:not(.mp-shell__rail--labeled) .mp-shell__brand {
  justify-content: center;
  padding-inline: 0;
}

/* Sized off the heading's own line box so the mark tracks the wordmark through
   both density scales rather than being pinned to one pixel value. */
.mp-shell__mark {
  flex: none;
  inline-size: 1.6em;
  block-size: 1.6em;
}

.mp-shell__main {
  flex: 1;
  min-inline-size: 0;
  padding: var(--space-6) var(--space-5);
}

.mp-shell__main:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

/* DESIGN.md section 12: at 1600+ the content stops growing. Rows stretched
   across a 2560px monitor are unreadable, not spacious. */
.mp-shell__content {
  max-inline-size: 96rem;
  margin-inline: auto;
}

.mp-shell__footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-5);
  background: var(--surface-sunken);
  border-block-start: 1px solid var(--border-subtle);
  font-size: var(--font-size-caption);
  color: var(--text-muted);
}

/* ── 360: nav is a fixed bottom bar, so the page owns the full width and has to
   reserve room above the bar for the footer. The rail is a top bar here — brand
   on one side, account on the other — and it sticks, because it is the only
   chrome a phone has and the tab bar below it is already fixed. ─────────── */
@media (max-width: 767px) {
  .mp-shell__body {
    flex-direction: column;
  }

  .mp-shell__rail {
    position: sticky;
    inset-block-start: 0;
    /* Under the toaster (50) and the skip link (100), over page content. The
       tab bar's own 40 resolves inside this stacking context, which changes
       nothing: no other positioned chrome sits between the two. */
    z-index: 30;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-4);
    padding-inline: var(--space-4);
    border-block-end: 1px solid color-mix(in srgb, var(--text-on-nav) 25%, transparent);
  }

  .mp-shell__main {
    padding: var(--space-5) var(--space-4);
  }

  .mp-shell__footer {
    margin-block-end: calc(var(--target-min) + var(--space-4));
  }
}

/* ── 768: the rail appears, and stops scrolling with the page. ─────────────
   `position: sticky` rather than `fixed`: fixed takes the rail out of flow, and
   the content column would then have to reserve its width by hand — two places
   that must agree about a width which changes with the label state. Sticky
   leaves that arithmetic to the flex row.

   `100dvh` against a sticky offset of 0 is what keeps the rail exactly one
   viewport tall wherever the page is scrolled to, and it is why `DevToolbar`
   moved inside the content column: a strip above the rail pushes the rail's
   foot one strip-height below the fold while the page is at the top, which
   clips the account panel in development and not in production — the worst
   place for a difference. Nothing may set `overflow` on an ancestor of the
   rail; that turns sticky off silently. */
@media (min-width: 768px) {
  .mp-shell__rail {
    position: sticky;
    inset-block-start: 0;
    flex: none;
    block-size: 100vh;
    block-size: 100dvh;
    /* The rail sets its own width so that the brand and the account panel
       cannot widen it past the nav; a long display name truncates instead. */
    inline-size: max-content;
  }

  /* DESIGN.md section 12: at 1280 the rail is persistent with labels. A labeled
     rail that hugs its longest label reads as cramped, so it gets a set width
     rather than a content width. */
  .mp-shell__rail--labeled {
    inline-size: 15rem;
    padding-inline: var(--space-3);
  }
}
</style>
